/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import JSZip from 'jszip';
import downloadImg from 'dndimages/download-icon.png';
import ObjectHelper from 'utils/ObjectHelper';
import Monster from 'model/monster/Monster';
import { fetchImageAsDataUrl } from 'utils/ImageUtils';

interface DownloadAllMonstersButtonProps {
  monsters: Monster[];
  prepMonsterForSave: (monster: Monster) => Record<string, any>;
}

const DownloadAllMonstersButton: React.FC<DownloadAllMonstersButtonProps> = ({ monsters, prepMonsterForSave }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [progress, setProgress] = React.useState({ current: 0, total: 0 });

  const handleDownload = async () => {
    if (!monsters || monsters.length === 0) {
      alert('No monsters to download');
      return;
    }

    try {
      setIsDownloading(true);
      setProgress({ current: 0, total: monsters.length });

      const zip = new JSZip();

      // Process all monsters in parallel
      const processedMonsters = await Promise.all(
        monsters.map(async (monster, index) => {
          const monsterCopy = structuredClone(monster);

          // If image is a Storage URL, fetch and embed it
          if (monsterCopy.image?.src &&
              (monsterCopy.image.src.startsWith('http://') || monsterCopy.image.src.startsWith('https://'))) {
            console.log(`Fetching Storage image for ${monsterCopy.name}...`);
            const dataUrl = await fetchImageAsDataUrl(monsterCopy.image.src);
            if (dataUrl) {
              monsterCopy.image.src = dataUrl;
            }
          }

          const data = prepMonsterForSave(monsterCopy);
          const cleanedData = ObjectHelper.removeEmpty(data);
          const sortedData = ObjectHelper.sortObjectKeys(cleanedData);

          // Update progress
          setProgress({ current: index + 1, total: monsters.length });

          return {
            name: monster.name,
            data: sortedData
          };
        })
      );

      // Add all processed monsters to ZIP
      processedMonsters.forEach(({ name, data }) => {
        const jsonStr = JSON.stringify(data, null, 2);
        // Sanitize filename to avoid issues
        const safeName = name.replace(/[/\\?%*:|"<>]/g, '_');
        zip.file(`${safeName}.json`, jsonStr);
      });

      // Generate the ZIP file
      const blob = await zip.generateAsync({ type: 'blob' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = 'my-monsters.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      alert('Failed to create ZIP file. Please try again.');
    } finally {
      setIsDownloading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <button
      className='myMonsterButton'
      style={{fontSize: 'x-small', padding: '2px'}}
      onClick={handleDownload}
      disabled={isDownloading}
      title={isDownloading ? 'Downloading monsters...' : 'Download all monsters as a ZIP file'}
    >
      <img src={downloadImg} alt="Download all monsters" width="16px" style={{paddingLeft: '8px'}}/>
      {isDownloading ? `Downloading... (${progress.current}/${progress.total})` : 'Download all monsters'}
    </button>
  );
};

export default DownloadAllMonstersButton;
